import { Product } from "@smile2impress/attraction-constants";
import type { DirectionType } from "templates/relaunch/MultiColumnSlider/MultiColumnSlider.types";

import { I18nLocale } from "constants/enum";

import { NextJSRedirect } from "./graphql/api/RedirectsApi";
import {
    Enum_Componentcomponentsleadform_Defaulttreatmenttype,
    Enum_Componentcomponentsleadform_Defaultpatientcategory,
} from "./generated/graphql";

/**
 * Frontend component types for CMS integration
 *
 * NOTE: These types are manually maintained to match the transformed data structure
 * after GraphQL → Frontend transformation (alias normalization, __typename → __component, etc.)
 *
 * Reference GraphQL types (auto-generated):
 * - ComponentComponentsMainBanner → ComponentMainBannerProps
 * - ComponentComponentsMainBannerExtra → ComponentMainBannerExtraProps
 * - etc. (see generated/graphql.ts)
 *
 * Type-safe Strapi props (derived from GraphQL fragments):
 * - StrapiMainBannerProps (from MainBannerFragmentFragment)
 * - StrapiMainBannerExtraProps (from MainBannerExtraFragmentFragment)
 * - etc. (see graphql/types/strapi-components.ts)
 *
 * The transformation pipeline:
 * 1. GraphQL query returns data with __typename and aliases (e.g., mainBannerTitle)
 * 2. mapComponentTypename: __typename → __component enum
 * 3. normalizeAliases: aliases → original field names (e.g., mainBannerTitle → title)
 * 4. transformComponent: GraphQL structure → Frontend structure
 * 5. Result matches these ComponentProps types
 *
 * Type safety:
 * - Input: Strapi*Props (type-safe GraphQL fragment types)
 * - Output: Component*Props (type-safe frontend component props)
 * - Transformers use these types for compile-time validation
 *
 * When adding new components:
 * 1. Add GraphQL fragment in queries/fragments/components/
 * 2. Add to ComponentProps union type below
 * 3. Ensure transformer handles the new component type
 * 4. Verify types match the transformed structure
 */

// Please keep this enum sorted alphabetically to make search easier
export enum ComponentsType {
    AWARDS = "components.awards",
    ADVANTAGES_WITH_HEADING = "components.advantages-with-heading",
    BANNER_PAID_SOCIAL = "components.banner-paid-social",
    CLINICS_LIST = "components.clinics-list",
    CLINICS_MAP = "components.clinics-map",
    FOLLOW_US = "components.follow-us",
    ILLUSTRATED_LIST = "components.illustrated-list",
    IMAGE_CARD_GRID = "components.image-card-grid",
    INFO_BANNER = "components.info-banner",
    LARGE_PICTURE = "components.large-picture",
    LIST_WITH_CTA = "components.list-with-cta",
    LEAD_FORM = "components.lead-form",
    MAIN_BANNER = "components.main-banner",
    MAIN_BANNER_HEADER = "components.main-banner-header",
    MAIN_BANNER_EXTRA = "components.main-banner-extra",
    MAP = "components.map",
    MULTI_COLUMN = "components.multi-column",
    MULTI_COLUMN_SLIDER = "components.multi-column-slider",
    PRICE_BLOCK = "components.price-block",
    QUESTIONS = "components.questions",
    REFERRAL_BANNER = "components.referral-banner",
    REFERRAL_HERO = "components.referral-hero",
    QUESTIONS_WITH_IMAGE = "components.questions-with-image",
    SLIDER = "components.slider",
    TABBED_CONTENT = "components.tabbed-content",
    TABLE_WITH_CHECKMARKS = "components.table-with-checkmarks",
    TERM_LINK = "components.term-link",
    TEXT_WITH_CTA = "components.text-with-cta",
    TEXT_WITH_CTA_FIFTY_FIFTY = "components.text-with-cta-fifty-fifty",
    TREATMENT_INFO = "components.treatment-info",
    TWO_COLUMN_LIST = "components.two-column-list",
    VIDEO = "components.video",
    VERTICAL_ADVANTAGES = "components.vertical-advantages",
    HALF_TEXT_AND_IMAGE_BANNER = "components.half-text-and-image-banner",
    VERTICAL_LIST_WITH_IMAGE = "components.vertical-list-with-image",
    VERTICAL_LIST_SECTION = "components.vertical-list-section",
    TREATMENT_INCLUDES_SECTION = "components.treatment-includes-section",
}

export interface BaseComponentProps<T> {
    id: number;
    __component: T;
    /** When true, Container in cmsComponentMatcher has no margin-bottom (default: false). */
    noMarginBottom?: boolean;
}

export type ComponentAwardsProps = BaseComponentProps<ComponentsType.AWARDS>;

export interface ElementIconProps {
    type: string;
}

export interface ElementTextColorProps {
    type: string;
}

export interface ElementTextProps {
    text: string;
}

export interface ElementBaseColorProps {
    type: string;
}

export interface ElementCtaProps {
    text: string;
    variant?: string[] | string;
    link?: { href: string };
    iconRight?: { type: string };
}

export interface ElementResponsiveImageProps {
    desktop: { url: string };
    mobile: { url: string };
}

export interface MainBannerAdvantage {
    title: string;
    subtitle: string;
}

export interface ComponentMainBannerProps
    extends BaseComponentProps<ComponentsType.MAIN_BANNER> {
    title: string;
    text: string;
    subtitle?: string;
    cta: ElementCtaProps;
    image: ElementResponsiveImageProps;
    advantages?: MainBannerAdvantage[];
    slogans?: { text: string }[];
    invisalign?: boolean;
    forceNoExtraContent?: boolean | null;
}

export interface ComponentBannerPaidSocialProps
    extends BaseComponentProps<ComponentsType.BANNER_PAID_SOCIAL> {
    title: string;
    text?: string;
    badge?: string;
    cta: ElementCtaProps;
    image: ElementResponsiveImageProps;
    advantages?: MainBannerAdvantage[];
    showInvisalignLogo?: boolean;
}

export interface ComponentMainBannerExtraProps
    extends BaseComponentProps<ComponentsType.MAIN_BANNER_EXTRA> {
    title: string;
    advantages: { text: string }[];

    disclaimer?: string;
    trustpilotLink?: { href: string };
    invisalign?: boolean;
}

export interface ComponentMainBannerHeaderProps
    extends BaseComponentProps<ComponentsType.MAIN_BANNER_HEADER> {
    title: string;
    text: string;
    cta?: ElementCtaProps;
    subtitle?: string;
}

export interface ComponentClinicsListProps
    extends BaseComponentProps<ComponentsType.CLINICS_LIST> {
    titleForFirstList: string;
    titleForSecondList?: string;
    titleForThirdList?: string;
    disclaimerForSecondList?: string;
}

export interface ComponentClinicsMapProps
    extends BaseComponentProps<ComponentsType.CLINICS_MAP> {
    title: string;
    image: {
        url: string;
        width?: number;
        height?: number;
        alternativeText?: string;
    };
}

export interface ComponentTwoColumnListProps
    extends BaseComponentProps<ComponentsType.TWO_COLUMN_LIST> {
    title: string;
    items: {
        title: string;
        text?: string;
        iconOnTop?: boolean;
        icon?: ElementIconProps;
        link?: { href: string };
    }[];
    subtitle?: string;
    withMobileList?: boolean;
}

export interface ComponentTextWithCTAProps
    extends BaseComponentProps<ComponentsType.TEXT_WITH_CTA> {
    title: string;
    text: string;
    cta: ElementCtaProps;
    image?: ElementResponsiveImageProps;
}

export interface ComponentTextWithCTAFiftyFiftyProps
    extends BaseComponentProps<ComponentsType.TEXT_WITH_CTA_FIFTY_FIFTY> {
    text: string;
    cta: ElementCtaProps;
    title?: string;
    textColor?: ElementTextColorProps;
    bgColor?: ElementBaseColorProps;
    icon?: ElementIconProps;
}

export interface ComponentAdvantagesWithHeadingProps
    extends BaseComponentProps<ComponentsType.ADVANTAGES_WITH_HEADING> {
    title: string;
    subtitle?: string;
    cta: ElementCtaProps;
    advantages: {
        title: string;
        subtitle: string;
    }[];
    disclaimer?: string;
}

export interface ComponentMultiColumnSliderProps
    extends BaseComponentProps<ComponentsType.MULTI_COLUMN_SLIDER> {
    title: string;
    subtitle?: string;
    disclaimer?: string;
    items: {
        image: ElementResponsiveImageProps[];
        captions?: { text: string }[];
    }[];
    cta?: ElementCtaProps;
    withExpansion?: boolean;
    mobileSwiper?: boolean;
    direction?: DirectionType;
}

export interface ComponentQuestionsWithImageProps
    extends BaseComponentProps<ComponentsType.QUESTIONS_WITH_IMAGE> {
    title: string;
    image: ElementResponsiveImageProps;
    questions: {
        items: {
            question: string;
            answer: string;
            icon?: ElementIconProps;
            large?: boolean;
        }[];
        title?: string;
        large?: boolean;
    };
    cta: ElementCtaProps;
    ctaTestId?: string;
}

export interface ComponentMapProps
    extends BaseComponentProps<ComponentsType.MAP> {
    title: string;
}

export interface ComponentQuestionsProps
    extends BaseComponentProps<ComponentsType.QUESTIONS> {
    title?: string;
    items: {
        question: string;
        answer: string;
        icon?: ElementIconProps;
        large?: boolean;
    }[];
}

export interface ComponentLargePictureProps
    extends BaseComponentProps<ComponentsType.LARGE_PICTURE> {
    image: ElementResponsiveImageProps;
}

export type ComponentProps =
    | ComponentAwardsProps
    | ComponentBannerPaidSocialProps
    | ComponentMainBannerProps
    | ComponentMainBannerExtraProps
    | ComponentMainBannerHeaderProps
    | ComponentClinicsListProps
    | ComponentClinicsMapProps
    | ComponentTwoColumnListProps
    | ComponentTextWithCTAProps
    | ComponentTextWithCTAFiftyFiftyProps
    | ComponentAdvantagesWithHeadingProps
    | ComponentMultiColumnSliderProps
    | ComponentMultiColumnProps
    | ComponentQuestionsWithImageProps
    | ComponentMapProps
    | ComponentQuestionsProps
    | ComponentLargePictureProps
    | ComponentPriceBlockProps
    | ComponentLeadFormProps
    | ComponentTreatmentInfoProps
    | ComponentVerticalAdvantagesProps
    | ComponentHalfTextAndImageBannerProps
    | ComponentVerticalListWithImageProps
    | ComponentVerticalListSectionProps
    | ComponentTreatmentIncludesSectionProps
    | ComponentIllustratedListProps
    | ComponentImageCardGridProps
    | ComponentSliderProps
    | ComponentTabbedContentProps
    | ComponentTableWithCheckmarksProps
    | ComponentInfoBannerProps
    | ComponentListWithCTAProps
    | ComponentFollowUsProps
    | ComponentVideoProps
    | ComponentReferralBannerProps
    | ComponentReferralHeroProps
    | ComponentTermLinkProps;

/** SEO open-graph block from CMS (shared.open-graph) */
export interface CmsPageOpenGraphProps {
    ogTitle: string;
    ogDescription: string;
    ogType?: string | null;
    ogUrl?: string | null;
    ogImage?: {
        url: string;
        width?: number | null;
        height?: number | null;
    } | null;
}

/** SEO block from CMS (shared.seo) — per locale */
export interface CmsPageSeoProps {
    metaTitle: string;
    metaDescription: string;
    canonicalURL?: string | null;
    metaRobots?: string | null;
    keywords?: string | null;
    metaViewport?: string | null;
    metaImage?: {
        url: string;
        width?: number | null;
        height?: number | null;
    } | null;
    openGraph?: CmsPageOpenGraphProps | null;
}

export interface CmsWrapperProps {
    data: CmsPageContentProps[];
}

export interface CmsRedirectsWrapperProps {
    data: NextJSRedirect[];
}

export interface CmsPageContentProps {
    id: number;
    documentId: string;
    name: string;
    slug: string;
    locale: I18nLocale;
    seo?: CmsPageSeoProps | null;
    noFooterMarginTop?: boolean;
    withHeader?: boolean;
    withFooter?: boolean;
    darkMode?: boolean;
    hideMenu?: boolean;
    localizations?: {
        slug: string;
        locale: I18nLocale;
    }[];
    components?: {
        selected: ComponentProps[];
    }[];
}

export interface CmsPagesProps {
    components: ComponentProps[];
    isPreview?: boolean;
    locations: null | {
        cities: any[];
        clinics: any[];
        flagshipClinics: any[];
    };
    products?: Product[];
    /** Passed to layout for MetaTags */
    seoTitle?: string;
    seoDescription?: string;
    seo?: CmsPageSeoProps | null;
    hreflangAlternates?: Array<{ href: string; hrefLang: string }>;
}

export interface CmsStaticProps {
    params: { slug?: string[] };
    locale: I18nLocale;
}

export enum CmsPagePreviewStatus {
    PUBLISHED = "published",
    DRAFT = "draft",
}

export interface ComponentPriceBlockProps
    extends BaseComponentProps<ComponentsType.PRICE_BLOCK> {
    title: string;

    subtitle?: string;
    hideCasesList?: boolean;
    hideMonthlyPrice?: boolean;
    hideDurationGraph?: boolean;
    hideBiteCorrection?: boolean;
    hideCaseFullName?: boolean;
    alwaysShowTnC?: boolean;
    casesList?: { text: string }[];
    detailsList?: { label: string; value: string }[];
}

export interface ComponentLeadFormProps
    extends BaseComponentProps<ComponentsType.LEAD_FORM> {
    title: string;
    image: ElementResponsiveImageProps;
    showTreatmentTypeSelector?: boolean;
    defaultTreatmentType?: Enum_Componentcomponentsleadform_Defaulttreatmenttype;
    showPromoCodeCTA?: boolean;
    showPatientCategorySelector?: boolean;
    defaultPatientCategory?: Enum_Componentcomponentsleadform_Defaultpatientcategory;
}

export interface ComponentTreatmentInfoItemProps {
    title: string;
    description: string;
    image?: ElementResponsiveImageProps;
    bgColor?: ElementBaseColorProps;
}
export interface ComponentTreatmentInfoProps
    extends BaseComponentProps<ComponentsType.TREATMENT_INFO> {
    title: string;
    subtitle: string;
    items: ComponentTreatmentInfoItemProps[];
}

export interface ComponentMultiColumnItemProps {
    title: string;
    text: string;
    image: ElementResponsiveImageProps;
    link?: { href: string };
    icon?: ElementIconProps;
    iconLocale?: string;
}

export enum ComponentMultiColumnColumnType {
    PRODUCTS = "Products",
    LONG_TEXT = "Long_text",
    ICONS = "Icons",
    DOCTORS = "Doctors",
}
export interface ComponentMultiColumnProps
    extends BaseComponentProps<ComponentsType.MULTI_COLUMN> {
    title?: string;
    subtitle?: string;
    items: ComponentMultiColumnItemProps[];
    columnType: ComponentMultiColumnColumnType;
    mobileSlider?: boolean;
    narrowColumns?: boolean;
    gridSpacing?: string;
    gridRows?: number;
    mobileGridRows?: number;
    gridRowGap?: string;

    cta?: ElementCtaProps;
}

export interface ComponentIllustratedListItemProps {
    title: string;
    text: string;
    image: ElementResponsiveImageProps;
    link?: { href: string };
    numberHeading?: string | number;
}

export interface ComponentIllustratedListProps
    extends BaseComponentProps<ComponentsType.ILLUSTRATED_LIST> {
    title: string;
    items: ComponentIllustratedListItemProps[];

    subtitle?: string;
    text?: string;
    cta?: ElementCtaProps;
    mainCTA?: ElementCtaProps;
    withNumberHeading?: boolean;
    withHeaderCta?: boolean;
    withMobileCta?: boolean;
    withDivider?: boolean;
    withExpandableText?: boolean;
}

/** Matches `Enum_Componentcomponentstabbedcontent_Type` from CMS GraphQL. */
export type ComponentTabbedContentLayoutType =
    | "FOUR_COLUMN"
    | "TWO_THREE_COLUMN";

export interface ComponentTabbedContentTabItemProps {
    image?: ElementResponsiveImageProps;
    label?: string;
    title?: string;
    text?: string;
    rating?: 1 | 2 | 3 | 4 | 5;
    customerName?: string;
    date?: string;
}

export interface ComponentTabbedContentTabProps {
    title: string;
    subtitle?: string;
    content: ComponentTabbedContentTabItemProps[];
}

export interface ComponentTabbedContentHeaderItemProps {
    title: string;
    description: string;
}

export interface ComponentTabbedContentProps
    extends BaseComponentProps<ComponentsType.TABBED_CONTENT> {
    content: {
        header: ComponentTabbedContentHeaderItemProps[];
        tabs: ComponentTabbedContentTabProps[];
        showMoreButtonLabel: string;
        rating?: 1 | 2 | 3 | 4 | 5;
        disclaimer?: string;
    };
    type: ComponentTabbedContentLayoutType;
}

export interface ComponentSliderItemProps {
    title: string;
    text?: string;
    rating: 1 | 2 | 3 | 4 | 5;
    customerName: string;
    date: string;
}

export interface ComponentSliderProps
    extends BaseComponentProps<ComponentsType.SLIDER> {
    title: string;
    items: ComponentSliderItemProps[];
    trustpilotLink?: string;
    cta?: ElementCtaProps;
    disclaimer?: string;
}

export interface ComponentTableWithCheckmarksRowProps {
    title: string;
    columns: boolean[];
}

export interface ComponentTableWithCheckmarksProps
    extends BaseComponentProps<ComponentsType.TABLE_WITH_CHECKMARKS> {
    title: string;
    columns: string[];
    rows: ComponentTableWithCheckmarksRowProps[];
    cta: ElementCtaProps;
}

export interface ComponentInfoBannerItemProps {
    text: string;
    icon?: ElementIconProps;
}

export interface ComponentImageCardItemProps {
    title: string;
    text: string;
    bg: ElementResponsiveImageProps;
}

export interface ComponentImageCardGridProps
    extends BaseComponentProps<ComponentsType.IMAGE_CARD_GRID> {
    title: string;
    items: ComponentImageCardItemProps[];
}

export interface ComponentInfoBannerProps
    extends BaseComponentProps<ComponentsType.INFO_BANNER> {
    title: string;
    text?: string;
    bg?: ElementBaseColorProps;
    iconColor?: ElementBaseColorProps;
    titleColor?: ElementTextColorProps;
    icon?: ElementIconProps;
    items?: ComponentInfoBannerItemProps[];
    cta?: ElementCtaProps;
}

export interface ComponentListWithCTAProps
    extends BaseComponentProps<ComponentsType.LIST_WITH_CTA> {
    title: string;
    text?: string;
    items?: ComponentInfoBannerItemProps[];
    cta?: ElementCtaProps;
}

export interface ComponentFollowUsSocialItemProps {
    icon?: ElementIconProps;
    link: string;
}

export interface ComponentFollowUsProps
    extends BaseComponentProps<ComponentsType.FOLLOW_US> {
    title: string;
    subtitle?: string;
    socials?: ComponentFollowUsSocialItemProps[];
}

export interface ComponentVerticalAdvantageItemProps {
    title: string;
    subtitle: string;
}
export interface ComponentVerticalAdvantagesProps
    extends BaseComponentProps<ComponentsType.VERTICAL_ADVANTAGES> {
    title: string;
    subtitle: string;

    cta?: ElementCtaProps;
    advantages?: ComponentVerticalAdvantageItemProps[];
    image?: ElementResponsiveImageProps;
}

export interface ComponentHalfTextAndImageBannerProps
    extends BaseComponentProps<ComponentsType.HALF_TEXT_AND_IMAGE_BANNER> {
    title: string;
    subtitle: string;
    image?: ElementResponsiveImageProps;
}

export interface ComponentVideoProps
    extends BaseComponentProps<ComponentsType.VIDEO> {
    title?: string;
    text?: string;
    caption?: string;
    videoUrl: string;
    videoUrlMobile?: string;
    poster?: ElementResponsiveImageProps;
}

export interface ElementGiftCardProps {
    title: string;
    text?: string;
    bgColor?: string;
    textColor?: string;
    image?: {
        url: string;
        width?: number;
        height?: number;
        alternativeText?: string;
    };
}

export interface ComponentReferralBannerProps
    extends BaseComponentProps<ComponentsType.REFERRAL_BANNER> {
    title: string;
    subtitle?: string;
    description?: string;
    buttonText: string;
    toastText?: string;
    cards?: ElementGiftCardProps[];
}

export interface ComponentReferralHeroProps
    extends BaseComponentProps<ComponentsType.REFERRAL_HERO> {
    title: string;
    subtitle?: string;
    description?: string;
    card?: ElementGiftCardProps;
}

export interface ComponentTermLinkProps
    extends BaseComponentProps<ComponentsType.TERM_LINK> {
    text: string;
}

export interface ComponentVerticalListWithImageItemProps {
    title: string;
    text?: string;
    icon?: ElementIconProps;
}

export interface ComponentVerticalListSectionItemProps {
    title: string;
    texts?: ElementTextProps[];
    icon?: ElementIconProps;
}

export interface ComponentVerticalListSectionProps
    extends BaseComponentProps<ComponentsType.VERTICAL_LIST_SECTION> {
    title: string;
    items: ComponentVerticalListSectionItemProps[];
    subtitle?: string;
    cta?: ElementCtaProps;
}

export interface ComponentTreatmentIncludesSectionProps
    extends BaseComponentProps<ComponentsType.TREATMENT_INCLUDES_SECTION> {
    title: string;
    image: ElementResponsiveImageProps;
    items: ElementTextProps[];
}

export interface ComponentVerticalListWithImageProps
    extends BaseComponentProps<ComponentsType.VERTICAL_LIST_WITH_IMAGE> {
    title: string;
    bgImage?: ElementResponsiveImageProps;
    items: ComponentVerticalListWithImageItemProps[];
    subtitle?: string;
    cta?: ElementCtaProps;
    numberedItems?: boolean;
}
